import React, { useState } from "react";
import { ActivityType, ItineraryActivity } from "../models/activity.model";
import { ModalCallbacks } from "../models/activities-modal.model";
import { getActivityTypeIcon } from "../models/activity-configs.model";
import AccommodationModal from "../modals/AccommodationModal";
import ActivityModal from "../modals/ActivityModal";
import FlightModal from "../modals/FlightModal";
import TransportationModal from "../modals/TransportationModal";
import FoodDrinksModal from "../modals/FoodDrinksModal";
import EventsModal from "../modals/EventsModal";
import NotesModal from "../modals/NotesModal";
import { AccommodationChoiceModal } from "../modals/AccommodationChoiceModal";
import { RateHawkAccommodationModal } from "../modals/RateHawkAccommodationModal";
import { ActivitySourceChoiceModal } from "../modals/ActivitySourceChoiceModal";
import { LibraryPickerModal } from "../modals/LibraryPickerModal";
import { createCheckInOutActivities } from "@/components/utils/createCheckOutFromCheckIn";
import type { IGuest } from "@/lib/request-trip/models/travel-request.model";
import type { DayAxis } from "../models/day-axis";

interface UseModalCallbacksProps {
  onSave: (activity: ItineraryActivity, isEditing?: boolean, editingActivityId?: string) => void;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: decide se le modali chiedono date reali o "Giorno N". */
  axis?: DayAxis | null;
  itineraryId: string;
  destinations?: string[];
  guestsData?: IGuest[];
  totalGuests?: number;
  selectedDate?: string;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" non sono piu' modificabili. */
  isPricingLocked?: boolean;
}

export const useModalCallbacks = ({ onSave, itineraryStartDate, itineraryEndDate, axis, itineraryId, destinations, guestsData, totalGuests, selectedDate, isPricingLocked = false }: UseModalCallbacksProps): ModalCallbacks => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState<ActivityType | null>(null);
  const [editingActivity, setEditingActivity] = useState<ItineraryActivity | null>(null);
  // true quando la modale è aperta con dati precompilati da una entry di libreria:
  // i campi sono valorizzati ma l'attività va INSERITA (non è un'attività esistente
  // in modifica), quindi `editingActivity` non deve far scattare la logica di edit.
  const [isLibraryPrefill, setIsLibraryPrefill] = useState(false);

  // Accommodation choice flow
  const [isChoiceModalOpen, setIsChoiceModalOpen] = useState(false);
  const [isRateHawkModalOpen, setIsRateHawkModalOpen] = useState(false);

  // Generic "create manually / choose from library" flow, for new activities of
  // any type other than accommodation (which already has its own choice modal above).
  const [isSourceChoiceModalOpen, setIsSourceChoiceModalOpen] = useState(false);
  const [sourceChoiceType, setSourceChoiceType] = useState<ActivityType | null>(null);

  // Library picker flow, reached either from the accommodation choice modal or from
  // the generic source choice modal above.
  const [isLibraryPickerOpen, setIsLibraryPickerOpen] = useState(false);
  const [libraryPickerType, setLibraryPickerType] = useState<ActivityType | null>(null);

  const openModal = (type: ActivityType, icon?: React.ReactNode, activityData?: ItineraryActivity) => {
    // For new accommodations (not editing), show the accommodation-specific choice
    // modal (manual / RateHawk booking portal / library)
    if (type === 'accommodation' && !activityData) {
      setIsChoiceModalOpen(true);
      return;
    }

    // For any other new activity (not editing), offer a quick choice between
    // creating it manually or picking an existing entry from the activity library.
    if (!activityData) {
      setSourceChoiceType(type);
      setIsSourceChoiceModalOpen(true);
      return;
    }

    setSelectedActivityType(type);
    setEditingActivity(activityData || null);
    setIsLibraryPrefill(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedActivityType(null);
    setEditingActivity(null);
    setIsLibraryPrefill(false);
  };

  const handleChooseManual = () => {
    setIsChoiceModalOpen(false);
    setSelectedActivityType('accommodation');
    setEditingActivity(null);
    setIsModalOpen(true);
  };

  const handleChooseRateHawk = () => {
    setIsChoiceModalOpen(false);
    setIsRateHawkModalOpen(true);
  };

  const handleChooseLibraryForAccommodation = () => {
    setIsChoiceModalOpen(false);
    setLibraryPickerType('accommodation');
    setIsLibraryPickerOpen(true);
  };

  const handleRateHawkSave = (activityData: Partial<ItineraryActivity>) => {
    setIsRateHawkModalOpen(false);
    onSave(activityData as ItineraryActivity, false, undefined);
  };

  const closeSourceChoiceModal = () => {
    setIsSourceChoiceModalOpen(false);
    setSourceChoiceType(null);
  };

  const handleChooseManualFromSourceChoice = () => {
    const type = sourceChoiceType;
    closeSourceChoiceModal();
    if (!type) return;
    setSelectedActivityType(type);
    setEditingActivity(null);
    setIsModalOpen(true);
  };

  const handleChooseLibraryFromSourceChoice = () => {
    const type = sourceChoiceType;
    closeSourceChoiceModal();
    if (!type) return;
    setLibraryPickerType(type);
    setIsLibraryPickerOpen(true);
  };

  const closeLibraryPicker = () => {
    setIsLibraryPickerOpen(false);
    setLibraryPickerType(null);
  };

  const handleLibraryActivitySelected = (activity: ItineraryActivity) => {
    closeLibraryPicker();
    // Non aggiungiamo l'attività direttamente: apriamo la modale di inserimento
    // del tipo corrispondente con i campi già precompilati dalla libreria, così
    // l'utente può rivederli/modificarli prima di salvare.
    // La DATA non viene precompilata: pur restando persistita in tabella, la lasciamo
    // vuota in fase di inserimento perché la valorizzi l'utente (deve indicare in che
    // giorno dell'itinerario cade questa attività, non quello della entry di libreria).
    activity.date = '';
    activity.start_date = undefined;
    activity.end_date = undefined;
    activity.start_day_index = null;
    activity.end_day_index = null;
    setSelectedActivityType(activity.type);
    setEditingActivity(activity);
    setIsLibraryPrefill(true);
    setIsModalOpen(true);
  };

  const handleSave = (activityData: Partial<ItineraryActivity>) => {
    // In caso di precompilazione da libreria si tratta di un nuovo inserimento,
    // non di una modifica: non passare l'id come `editingActivityId`.
    const isEditing = editingActivity !== null && !isLibraryPrefill;
    const editingActivityId = isEditing ? editingActivity?.id : undefined;

    onSave(activityData as ItineraryActivity, isEditing, editingActivityId);
  };

  const renderModal = (): React.ReactNode => {
    const modals: React.ReactNode[] = [];

    // Choice modal
    modals.push(
      React.createElement(AccommodationChoiceModal, {
        key: 'accommodation-choice',
        isOpen: isChoiceModalOpen,
        onClose: () => setIsChoiceModalOpen(false),
        onChooseManual: handleChooseManual,
        onChooseRateHawk: handleChooseRateHawk,
        onChooseLibrary: handleChooseLibraryForAccommodation,
      })
    );

    // RateHawk modal
    modals.push(
      React.createElement(RateHawkAccommodationModal, {
        key: 'ratehawk-accommodation',
        isOpen: isRateHawkModalOpen,
        onClose: () => setIsRateHawkModalOpen(false),
        onSave: handleRateHawkSave,
        destinations,
        startDate: itineraryStartDate,
        endDate: itineraryEndDate,
        axis,
        currentDayDate: selectedDate,
        guestsData,
        totalGuests,
      })
    );

    // Generic "manual / library" choice, for new activities of any type other
    // than accommodation. Only mounted once a type has been picked, so its
    // library query (in LibraryPickerModal below) isn't fired needlessly.
    if (sourceChoiceType) {
      modals.push(
        React.createElement(ActivitySourceChoiceModal, {
          key: 'activity-source-choice',
          isOpen: isSourceChoiceModalOpen,
          onClose: closeSourceChoiceModal,
          activityType: sourceChoiceType,
          onChooseManual: handleChooseManualFromSourceChoice,
          onChooseLibrary: handleChooseLibraryFromSourceChoice,
        })
      );
    }

    // Library picker, reached from either choice modal above.
    if (libraryPickerType) {
      modals.push(
        React.createElement(LibraryPickerModal, {
          key: 'library-picker',
          isOpen: isLibraryPickerOpen,
          onClose: closeLibraryPicker,
          activityType: libraryPickerType,
          date: selectedDate,
          onSelect: handleLibraryActivitySelected,
        })
      );
    }

    // Standard activity modals
    if (!selectedActivityType) return React.createElement(React.Fragment, null, ...modals);

    const icon = getActivityTypeIcon(selectedActivityType);

    const commonProps = {
      isOpen: isModalOpen,
      onClose: closeModal,
      onSave: handleSave,
      icon,
      itineraryStartDate,
      itineraryEndDate,
      axis,
      itineraryId,
      defaultDate: selectedDate || undefined,
      // Unico punto in cui il flag entra nelle modali attivita': sono sei, e passarlo qui
      // evita di doverselo ricordare tipo per tipo.
      pricingLocked: isPricingLocked
    };

    let activityModal: React.ReactNode = null;
    switch (selectedActivityType) {
      case 'accommodation':
        activityModal = React.createElement(AccommodationModal, {
          ...commonProps,
          initialData: (editingActivity?.type.startsWith('accommodation')) ? editingActivity : undefined
        });
        break;
      case 'activity':
        activityModal = React.createElement(ActivityModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'activity' ? editingActivity : undefined
        });
        break;
      case 'flight':
        activityModal = React.createElement(FlightModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'flight' ? editingActivity : undefined
        });
        break;
      case 'transportation':
        activityModal = React.createElement(TransportationModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'transportation' ? editingActivity : undefined
        });
        break;
      case 'food-drinks':
        activityModal = React.createElement(FoodDrinksModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'food-drinks' ? editingActivity : undefined
        });
        break;
      case 'events':
        activityModal = React.createElement(EventsModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'events' ? editingActivity : undefined
        });
        break;
      case 'notes':
        activityModal = React.createElement(NotesModal, {
          ...commonProps,
          initialData: editingActivity?.type === 'notes' ? editingActivity : undefined
        });
        break;
    }

    modals.push(activityModal);
    return React.createElement(React.Fragment, null, ...modals);
  };

  return {
    openModal,
    closeModal,
    renderModal,
    isModalOpen: isModalOpen || isChoiceModalOpen || isRateHawkModalOpen || isSourceChoiceModalOpen || isLibraryPickerOpen,
    selectedActivityType
  };
};
